import { Component, OnInit, OnDestroy, EventEmitter } from '@angular/core';
import { ProjectCommunicationService } from '../../../communication/project-comms.service';
import { ConfiguredToastrService } from '../../../helper/toastr/configured-toastr.service';
import { UnitService, Unit } from '../../../helper/anessa-library/anessa-library/projects/unit-library/src/public-api';
import { takeUntil } from 'rxjs/operators';
import Utils from '../../../util';
import { Subject } from 'rxjs';
import { AnessaTranslationInterface } from '../../../models/interfaces/anessa-translation.interface';
import { AnessaTranslationService } from '../../../helper/translation/anessa-translation.service';
import { DropdownDisplayItem } from '../../../models/data-structures/dropdown-display-item';
import { AggregationMethod } from '../../results-helper/grouped-result-table/grouped-result-table.component';
import { UserpilotReloader } from '../../../helper/userpilot-reloader.component';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { DebtScheduleInterface, LoanResult, LoanResultKeys } from '../../../models/results/financial/debt-schedule.model';
import { ResultRow } from '../../../models/data-structures/results/result-row.model';
import { LooseObject } from '../../../models/loose-object.model';
import { PlotlyTrace } from '../../../models/results/plotly.model';

@Component({
  selector: 'app-baseline-debt-schedule',
  templateUrl: './baseline-debt-schedule.component.html',
  styleUrls: ['./baseline-debt-schedule.component.scss']
})
export class BaselineDebtScheduleComponent extends UserpilotReloader implements OnInit, OnDestroy, AnessaTranslationInterface {
  onDestroyNotifier = new EventEmitter();
  dataLoadedNotifier = new Subject<boolean>();

  /** Currency exchange rates list */
  currencyData: Unit[] = [];
  /** Date the exchange rates are from */
  exchangeRateDate?: string;
  /** Selected Currency */
  selectedCurrency?: Unit;
  /**
   * List of media types
   */
  mediaTypes: DropdownDisplayItem[] = [];
  /** Media type to display the results */
  mediaType = ['table'];

  /** List of results */
  resultOptions: {key: string, displayName: string}[] = [];

  /** Table data */
  tableData: Record<string, LoanResult> = {};
  /** Custom headers */
  tableHeaders: string[] = [];

  // values to calculate graph y axis ticks
  y1_max = 0;
  y2_max = 0;

  // graph setup variables
  graphData: PlotlyTrace[] = [];
  graphLayout?: Partial<Plotly.Layout>;
  showGraph = false;
  yAxisLabel: string = '';
  xAxisLabel: string = '';

  /** Selected loan */
  selectedResults: {key: string, displayName: string}[] = [];

  /** Financial section of the results */
  financialResults?: DebtScheduleInterface;

  trigger?: string;

  aggMethod = AggregationMethod.SUM;

  constructor(private projectCommService: ProjectCommunicationService,
              private toastrService: ConfiguredToastrService,
              private unitService: UnitService,
              private translationService: AnessaTranslationService
  ) {
    super();
  }

  ngOnInit(): void {
    this.setTranslations();
    this.projectCommService.resultsLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(loaded => {
        if (loaded) {
          this.setupResultSelect();
        }
      });

    this.dataLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(() => {
        if (this.currencyData) {
          // set default currency
          if (!this.selectedCurrency && this.projectCommService.getProjectInfo().defaults.currency) {
            this.selectedCurrency = this.currencyData.find(curr => curr.id === this.projectCommService.getProjectInfo().defaults.currency);
          }
          if (!this.selectedCurrency) {
            this.selectedCurrency = this.currencyData.find(curr => curr.id === '000000000000000000002004'); // CAD
          } else {
            this.selectedCurrency = this.currencyData.find(curr => {
              if (!this.selectedCurrency) return false;
              return curr.id === this.selectedCurrency.id
            });
          }
          this.mediaTypeChanged();
        }
      });
    this.translationService.languageChangeNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(language => {
        const oldSelection = Utils.deepCopy(this.selectedResults);
        this.setTranslations();
        this.setupResultSelect();
        this.restoreSelection(oldSelection);
        this.setTableData();
        this.trigger = language;
      });
  }

  ngOnDestroy(): void {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
  }

  setTranslations() {
    this.tableHeaders = [
      this.translationService.get('RESULTS.GENERAL.VALUE'),
      this.translationService.get('RESULTS.GENERAL.UNIT'),
    ];
    this.yAxisLabel = this.translationService.get('RESULTS.DEBT_SCHEDULE.Y_LABEL');
    this.xAxisLabel = this.translationService.get('RESULTS.GENERAL.YEARS');
    this.mediaTypes = [
      new DropdownDisplayItem('RESULTS.GENERAL.TABLE', 'table'),
      new DropdownDisplayItem('RESULTS.GENERAL.GRAPH', 'graph')
    ];
  }

  /**
   * Get the currency exchange rates
   * @param created date of exchange rates to get
   */
  getFinExchange(created: string | null) {
    this.unitService.getCurrencyUnits(created).then(
      (units: Unit[]) => {
        this.exchangeRateDate = this.unitService.getExchangeRateDate();
        this.currencyData = units;

        // sort rates alphabetically
        this.currencyData.sort((first: Unit, second: Unit) => {
          if (Number(first.order) < Number(second.order)) {
            return -1;
          } else if (Number(second.order) < Number(first.order)) {
            return 1;
          } else {
            return 0;
          }
        });
        this.dataLoadedNotifier.next(true);
      }
    );
  }

  /**
   * Setup function for the result select.
   */
  setupResultSelect(): void {
    this.selectedResults = [];
    const results = this.projectCommService.getResults();
    if (results && results.financial) {
      this.financialResults = results.financial.debtSchedule;
      if (!this.financialResults) {
        this.toastrService.warning(this.translationService.get('RESULTS.ERRORS.NO_RESULTS'));
        return;
      }
      this.getFinExchange(results.created);
      this.setResultSelectOptions();
      this.selectedResults = [...this.resultOptions];
    } else {
      this.toastrService.warning(
        this.translationService.get('RESULTS.ERRORS.PLEASE_SIMULATE'),
        this.translationService.get('RESULTS.ERRORS.NO_RESULTS'));
    }
  }

  /**
   * Creates custom options used for the result select
   */
  setResultSelectOptions(): void {
    this.resultOptions = [];
    if (!this.financialResults) return;
    for (const section of Object.keys(this.financialResults)) {
      if (section === 'RESULTS.DATA.TOTALS' || section === 'totals') {
        continue;
      }

      const sectionObj = this.financialResults[section];
      let translation = '';
      let displayName = '';
      if (sectionObj.formatValues) {
        displayName = section + ' ' + translation;
        translation = this.translationService.get(sectionObj.formatValues[0]);
      } else {
        translation = this.translationService.get(section);
        displayName = translation;
      }
      this.resultOptions.push({key: section, displayName: displayName});
    }
  }

  /**
   * Set result display based on media type selected
   */
  mediaTypeChanged(): void {
    if (this.mediaType.includes('table')) {
      this.setTableData();
    }
    if (this.mediaType.includes('graph')) {
      this.setGraphData();
    } else {
      this.graphData = [];
      this.showGraph = false;
    }
  }

  /**
   * Sets the table data to be dislayed
   */
  setTableData(): void {
    this.tableData = {};
    if (!this.selectedResults || !this.selectedResults.length || !this.financialResults) return;

    for (const selectedResult of this.selectedResults) {
      let loan = this.financialResults[selectedResult.key];
      loan = loan.deepCopy();
      for (const group of Object.keys(loan)) {
        if (group === 'formatValues') {
          delete loan[group];
        }
      }
      this.tableData[selectedResult.displayName] = loan;
    }
    this.convertValuesCurrency(this.tableData);
  }

  /**
   * Converts result data based on the select currency
   */
  convertValuesCurrency(result: LooseObject) {
    const leafKeys = ['RESULTS.DATA.UNIT', 'RESULTS.DATA.VALUES'];
    if (leafKeys.every(leafKey => Object.keys(result).includes(leafKey))) { // results object is a leaf
      const values = result.values;
      const currency = result.unit;
      for (let i = 0; i < values.length; i++) {
        values[i] = Unit.convertValue(values[i], currency, this.selectedCurrency.id);
      }
    } else {
      for (const subResult of Object.values(result)) {
        this.convertValuesCurrency(subResult);
      }
    }
  }

  /**
   * Checks if at least one result is selected
   */
  showTable(): boolean {
    if (this.mediaType.includes('table') && Object.keys(this.tableData).length > 0) {
      return true;
    }
    return false;
  }


  /**
   * Converts result data based on the select currency
   * @param row result data row
   */
  processExchange(row: ResultRow): any {
    // Regex explanation https://regex101.com/r/usddk5/1
    const combinedUnitRegex = /(00000000000000000000(20|22)\d{2})/;
    const currencyUnitRegex = /(0000000000000000000020\d{2})/;
    const cpiUnitRegex = /(0000000000000000000022\d{2})/;

    // Check if CPI or currency and convert appropriately
    if (row.unit && row.unit.match(combinedUnitRegex) && this.selectedCurrency != null) {
      let currencyUnitId: string;

      if (row.unit.match(currencyUnitRegex)) {
        currencyUnitId = row.unit;
      } else if (row.unit.match(cpiUnitRegex)) {
        const cpiUnit = this.unitService.getUnitById(row.unit);
        const currencyUnit = this.unitService.getUnitByName(cpiUnit.displayName.slice(0, 3));
        currencyUnitId = currencyUnit.id;
      }

      // Only convert if results and selected currency are different
      if (currencyUnitId && currencyUnitId !== this.selectedCurrency.id) {
        row.values[0] = Unit.convertValue(row.values[0], currencyUnitId, this.selectedCurrency.id);
        for (let i = 0; i < row.values.length; i++) {
          row.values[i] = Unit.convertValue(row.values[i], currencyUnitId, this.selectedCurrency.id);
        }
      }
    }
  }

  /**
   * Set the graph data to be displayed
   */
  setGraphData() {
    this.graphData = [];
    this.y1_max = 0;
    this.y2_max = 0;

    if (!this.financialResults) return;

    for (const selectedResult of this.selectedResults) {
      const loan = Utils.deepCopy(this.financialResults[selectedResult.key]);

      for (const section of Object.keys(loan) as LoanResultKeys[]) {
        const row = loan[section];
        if (!(row instanceof ResultRow)) continue;
        row.label = this.translationService.get(row.label);
        row.values = row.values;
        row.unit = row.unit;

        this.processExchange(row);

        const years = [];
        for (const year of Object.keys(row.values)) {
          years.push(year);
        }

        let trace: PlotlyTrace;
        trace = {
          x: [],
          y: [],
          name: section + ' - ' + selectedResult.key,
          type: 'bar'
        };

        // Add data to trace
        for (let i = 1; i < years.length; i++) {
          trace.x.push(i);
          trace.y.push(row.values[i]);
        }

        this.graphData.push(trace);

        // Get maximum y values for bars and curve
        this.y1_max = Math.max(...row.values, this.y1_max);
      }
    }

    this.setLayout();

    setTimeout(() => {
      this.showGraph = false;
    });
    setTimeout(() => {
      this.graphData = [...this.graphData];
      if (this.graphData && this.graphData.length > 0) {
        this.showGraph = true;
      }
    });
  }

  /**
   * Create the layout for the graph to have two Y axes
   */
  setLayout() {
    // ~~~~~~~~ Tick value calculations ~~~~~~~~
    // Y1 Calculations
    const y1_len = Math.floor(this.y1_max).toString().length;  // 4
    const y1_pow10_divisor = Math.pow(10, y1_len - 1);  // 1000
    const y1_firstdigit = Math.floor(this.y1_max / y1_pow10_divisor); // 2
    const y1_max_base = y1_pow10_divisor * y1_firstdigit;  // 2000

    const y1_dtick = y1_max_base / 5;  // 2000 / 5 == 400

    // Y2 Calculations
    const y2_len = Math.floor(this.y2_max).toString().length;  // 3
    const y2_pow10_divisor = Math.pow(10, y2_len - 1); // 100
    const y2_firstdigit = Math.floor(this.y2_max / y2_pow10_divisor); // 3
    const y2_max_base = y2_pow10_divisor * y2_firstdigit;  // 300

    const y2_dtick = y2_max_base / 5;  // 300 / 5 == 60


    // ~~~~~~~~ Align both Y axis ~~~~~~~~
    // Find initial dtick ratios for comparison
    const y1_dtick_ratio = this.y1_max / y1_dtick;  // 5.515
    const y2_dtick_ratio = this.y2_max / y2_dtick;  // 6.0873333333333335

    // Get the larger of the two ratios and add 0.1 to prevent cut off at highest point
    const global_dtick_ratio = Math.max(y1_dtick_ratio, y2_dtick_ratio) + 0.1; // 6.1873

    // ~~~~~~~~ Get maximum y values ~~~~~~~~
    const y1_range_max = global_dtick_ratio * y1_dtick;  // 6.1873 * 400 == 2434.9333
    const y2_range_max = global_dtick_ratio * y2_dtick;  // 6.1873 * 60 == 365.2400

    // ~~~~~~~~ Set graph layout data ~~~~~~~~
    this.yAxisLabel = this.translationService.get('RESULTS.DEBT_SCHEDULE.Y_LABEL');
    this.graphLayout = {
      autosize: true,
      width: 1000,
      height: 600,
      xaxis: { title: '', domain: [0, 0.9] },
      yaxis: {
        range: [0, y1_range_max],
        dtick: y1_dtick
      },
      title: '',
      legend: { 'orientation': 'h', x: -0.05, y: -0.3 },
      margin: { l: 50, r: 0, b: 70, t: 50 },
      yaxis2: {
        title: this.translationService.get('RESULTS.DEBT_SCHEDULE.GRAPH_TITLE'),
        overlaying: 'y',
        side: 'right',
        range: [0, y2_range_max],
        dtick: y2_dtick,
        position: 0.9
      }
    };
  }


  /**
   * Changes status of all options to selected or unselected
   * @param $event click event
   */
  inputsCheckAll($event: MatCheckboxChange): void {
    if ($event.checked) {
      this.selectedResults = [...this.resultOptions];
    } else {
      this.selectedResults = [];
    }
    this.mediaTypeChanged();
  }

  /**
   * Checks if all options are selected
   */
  allInputsSelected(): boolean {
    for (const item of this.resultOptions) {
      if (!this.selectedResults.includes(item)) {
        return false;
      }
    }
    return true;
  }

  /**
   * Checks if at least one option is selected
   */
  isCheckAllIndeterminate(): boolean {
    let atLeastOneIncluded = false;
    let allIncluded = true;
    for (const item of this.resultOptions) {
      if (!this.selectedResults.includes(item)) {
        atLeastOneIncluded = true;
      } else {
        allIncluded = false;
      }
    }

    return atLeastOneIncluded && !allIncluded;
  }

  private restoreSelection(oldSelection: {key: string, displayName: string}[]) {
    this.selectedResults = [];
    const selectedKeys = [];
    for (const oldVal of oldSelection) {
      selectedKeys.push(oldVal.key);
    }

    for (const item of this.resultOptions) {
      if (selectedKeys.includes(item.key)) {
        this.selectedResults.push(item);
      }
    }
  }
}
